/**
 * Leg-end and race-end scoring.
 *
 * Both functions are pure: they produce review payloads + a coin delta for
 * each player. The dispatcher in `moves.ts` is responsible for applying the
 * deltas back to player records and setting `status` / `legReview` /
 * `raceReview` / `winner` on the returned state.
 */

import type {
  CamelUpState,
  LegBetBreakdown,
  LegReview,
  LegReviewRow,
  RaceBetResolution,
  RaceReview,
  RaceReviewRow,
} from '../types'
import {
  LEG_BET_MISS_PAYOUT,
  LEG_BET_SECOND_PAYOUT,
  RACE_BET_LADDER,
  RACE_BET_MISS_PAYOUT,
} from './constants'
import { rankRacingCamels } from './movement'

// ---------------------------------------------------------------------------
// Leg end
// ---------------------------------------------------------------------------

export interface LegScoringResult {
  review: LegReview
  /** uid -> coin delta to apply for this leg. */
  deltas: Record<string, number>
}

/**
 * Score the just-completed leg. Computes:
 *   - rankings of the 5 racing camels at this moment,
 *   - pyramid-ticket coins (+1 per ticket each player took this leg),
 *   - leg-bet payouts per held ticket.
 *
 * Does NOT mutate state or apply deltas — caller folds them in.
 */
export function scoreLeg(state: CamelUpState): LegScoringResult {
  const rankings = rankRacingCamels(state)
  const first = rankings[0]
  const second = rankings[1]

  const deltas: Record<string, number> = {}
  const rows: LegReviewRow[] = []

  for (const uid of state.seatOrder) {
    const p = state.players[uid]
    const pyramidCoins = p.pyramidTicketsThisLeg
    let legBetCoins = 0
    const breakdown: LegBetBreakdown[] = []

    for (const ticket of p.legBets) {
      let payout: number
      if (ticket.color === first) payout = ticket.value
      else if (ticket.color === second) payout = LEG_BET_SECOND_PAYOUT
      else payout = LEG_BET_MISS_PAYOUT
      legBetCoins += payout
      breakdown.push({ color: ticket.color, value: ticket.value, payout })
    }

    const startCoins = p.coins
    // Coins floor at 0 in Camel Up (you can't go below 0). Clamp.
    const endCoins = Math.max(0, startCoins + pyramidCoins + legBetCoins)
    deltas[uid] = endCoins - startCoins

    rows.push({
      uid,
      startCoins,
      pyramidCoins,
      legBetCoins,
      endCoins,
      legBetBreakdown: breakdown,
    })
  }

  return {
    review: { leg: state.leg, rankings, rows },
    deltas,
  }
}

// ---------------------------------------------------------------------------
// Race end
// ---------------------------------------------------------------------------

export interface RaceScoringResult {
  review: RaceReview
  /** uid -> coin delta from race-bet resolution (added on top of leg deltas). */
  deltas: Record<string, number>
  winner: { uid: string; coins: number }
}

/**
 * Score the end of the race. Caller supplies the post-leg state (coins
 * already include the final leg's payouts) and the final rankings.
 *
 * Race-bet piles are walked in placement order; correct guesses earn the
 * `RACE_BET_LADDER` ladder (8/5/3/2/1, then 1 for any beyond 5th); wrong
 * guesses earn `RACE_BET_MISS_PAYOUT` (−1).
 *
 * The overall winner is the player with the most coins after all race-bet
 * resolutions; ties broken by `seatOrder`.
 */
export function scoreRace(state: CamelUpState): RaceScoringResult {
  const rankings = rankRacingCamels(state)
  const raceWinner = rankings[0]
  const raceLoser = rankings[rankings.length - 1]

  const deltas: Record<string, number> = {}
  for (const uid of state.seatOrder) deltas[uid] = 0

  const rowsByUid: Record<string, RaceReviewRow> = {}
  for (const uid of state.seatOrder) {
    rowsByUid[uid] = {
      uid,
      coinsBeforeRace: state.players[uid].coins,
      raceBetCoins: 0,
      finalCoins: state.players[uid].coins,
      bets: [],
    }
  }

  // Winner pile.
  state.racePiles.winner.forEach((slot, position) => {
    // Defensive: a slot with `color: null` means the service layer never
    // filled in the secret color (should never happen in the engine's view
    // of state). Skip with a console-quiet warning shape.
    if (slot.color === null) return
    const correct = slot.color === raceWinner
    const payout = correct
      ? RACE_BET_LADDER[Math.min(position, RACE_BET_LADDER.length - 1)]
      : RACE_BET_MISS_PAYOUT
    deltas[slot.uid] = (deltas[slot.uid] ?? 0) + payout
    const resolution: RaceBetResolution = {
      pile: 'winner',
      color: slot.color,
      uid: slot.uid,
      position,
      correct,
      payout,
    }
    rowsByUid[slot.uid].bets.push(resolution)
    rowsByUid[slot.uid].raceBetCoins += payout
  })

  // Loser pile.
  state.racePiles.loser.forEach((slot, position) => {
    if (slot.color === null) return
    const correct = slot.color === raceLoser
    const payout = correct
      ? RACE_BET_LADDER[Math.min(position, RACE_BET_LADDER.length - 1)]
      : RACE_BET_MISS_PAYOUT
    deltas[slot.uid] = (deltas[slot.uid] ?? 0) + payout
    const resolution: RaceBetResolution = {
      pile: 'loser',
      color: slot.color,
      uid: slot.uid,
      position,
      correct,
      payout,
    }
    rowsByUid[slot.uid].bets.push(resolution)
    rowsByUid[slot.uid].raceBetCoins += payout
  })

  // Apply deltas to `finalCoins` (clamped at 0).
  for (const uid of state.seatOrder) {
    rowsByUid[uid].finalCoins = Math.max(
      0,
      rowsByUid[uid].coinsBeforeRace + (deltas[uid] ?? 0),
    )
    // Re-derive delta from the clamp so the caller's coin update matches.
    deltas[uid] = rowsByUid[uid].finalCoins - rowsByUid[uid].coinsBeforeRace
  }

  // Pick the overall winner: most coins, tie-break by seat order.
  let winnerUid = state.seatOrder[0]
  let winnerCoins = rowsByUid[winnerUid].finalCoins
  for (const uid of state.seatOrder) {
    const c = rowsByUid[uid].finalCoins
    if (c > winnerCoins) {
      winnerUid = uid
      winnerCoins = c
    }
  }

  return {
    review: {
      raceWinner,
      raceLoser,
      rows: state.seatOrder.map((uid) => rowsByUid[uid]),
    },
    deltas,
    winner: { uid: winnerUid, coins: winnerCoins },
  }
}

/** Decide the race-bet ladder payout for a given placement order. */
export function raceBetLadder(position: number): number {
  return RACE_BET_LADDER[Math.min(position, RACE_BET_LADDER.length - 1)]
}

export { rankRacingCamels } from './movement'
